// ============================================================================
// JARVISTRAVEL - NOTIFICATIONS API
// Version: 1.0.0
// Notification CRUD — list, mark as read, create, action
// ============================================================================

import { api } from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NotificationType =
  | "trip-invite"
  | "booking"
  | "reminder"
  | "price-alert"
  | "weather"
  | "flight-alert"
  | "collaborator"
  | "system";

export interface NotificationData {
  id: string;
  user_id: string;
  trip_id?: string;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  dismissed: boolean;
  actionable: boolean;
  data?: Record<string, unknown>;
  created_at: string;
}

export interface ListNotificationsResponse {
  notifications: NotificationData[];
}

export interface CreateNotificationRequest {
  trip_id?: string;
  type?: NotificationType;
  title?: string;
  message: string;
  actionable?: boolean;
  data?: Record<string, unknown>;
}

export interface CreateNotificationResponse {
  notification_id: string;
}

export interface MarkAsReadResponse {
  success: boolean;
}

export type NotificationAction = 'accept' | 'decline';

export interface ActionNotificationResponse {
  success: boolean;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * List notifications for the current user.
 * GET /v1/notifications
 */
export async function listNotifications(): Promise<NotificationData[]> {
  const result = await api.get<ListNotificationsResponse>("/notifications");

  if (result.error) {
    throw new Error(result.error.message || "Failed to list notifications");
  }

  return result.data?.notifications || [];
}

/**
 * Create a new notification.
 * POST /v1/notifications
 */
export async function createNotification(
  req: CreateNotificationRequest,
): Promise<string> {
  const result = await api.post<CreateNotificationResponse>("/notifications", req);

  if (result.error) {
    throw new Error(result.error.message || "Failed to create notification");
  }

  return result.data?.notification_id || "";
}

/**
 * Mark a notification as read.
 * PUT /v1/notifications/{notificationId}/read
 */
export async function markNotificationRead(
  notificationId: string,
): Promise<boolean> {
  const result = await api.put<MarkAsReadResponse>(
    `/notifications/${notificationId}/read`,
  );

  if (result.error) {
    throw new Error(result.error.message || "Failed to mark notification as read");
  }

  return result.data?.success || false;
}

/**
 * Perform an action on a notification (accept/decline).
 * POST /v1/notifications/{notificationId}/action
 */
export async function actionNotification(
  notificationId: string,
  action: NotificationAction,
): Promise<boolean> {
  const result = await api.post<ActionNotificationResponse>(
    `/notifications/${notificationId}/action`,
    { action },
  );

  if (result.error) {
    throw new Error(result.error.message || "Failed to perform notification action");
  }

  return result.data?.success || false;
}

/**
 * Dismiss a notification (soft-delete).
 * POST /v1/notifications/{notificationId}/dismiss
 */
export async function dismissNotification(
  notificationId: string,
): Promise<boolean> {
  const result = await api.post<MarkAsReadResponse>(
    `/notifications/${notificationId}/dismiss`,
  );

  if (result.error) {
    throw new Error(result.error.message || "Failed to dismiss notification");
  }

  return result.data?.success || false;
}
